import React, { useState, useEffect, useContext } from 'react';
import { DownOutlined } from '@ant-design/icons';
import {
  Table,
  Button,
  Popconfirm,
  Empty,
  Menu,
  Dropdown,
  Drawer,
  message,
  Spin,
  Select,
  Skeleton,
} from 'antd';
import {
  SEARCH_PASSENGERS,
  FETCH_SERVICE_PASSENGERS_INFO,
  FETCH_USER_PASSENGERS,
} from '../services/queries';
import { sendEmail, constructEmail } from '../../utils/email';
import { DrawerProps } from 'antd/lib/drawer';
import { CreateNewPassengerForm } from './createNewPassengerForm';
import {
  ADD_PASSENGER_TO_SERVICE,
  REMOVE_PASSENGER_FROM_SERVICE,
} from './mutations';
import { MainContext } from '../../contexts/mainContext';
import { useQuery } from '@apollo/client';
import { getClient } from '../../apollo';
import { getMobileApk } from '../../utils/mobile';

const { Option } = Select;

const infoLoadedMap = {};

const openLoadingMessage = (text: string, key: string) => {
  message.loading({ content: text, key, duration: 0 });
};

const openSuccessMessage = (text: string, key: string) => {
  message.success({ content: text, key });
};

interface IOperation {
  text: string;
  deletesRow: boolean | false;
  action: (...args: any[]) => any;
}

interface IPassengersManagementState {
  dataSource: any[];
  count: number;
  createPassengerDrawerVisible: boolean;
}

interface IPassengersManagementProps {
  refetch: any;
  clientId: string;
  serviceId: string;
  passengers: IPassenger[];
}

interface IRow extends IPassenger {
  key: string;
}

interface IPassenger {
  email: string;
  phone?: string;
  id: string;
  name: string;
}

interface PassengerManagementContainerProps {
  serviceId: string;
  clientId: string;
}

const PassengersManagementContainer = (
  props: PassengerManagementContainerProps,
) => {
  const context = useContext(MainContext);

  const servicePassengers = useQuery(FETCH_SERVICE_PASSENGERS_INFO, {
    variables: {
      id: props.serviceId,
    },
    pollInterval: 4000,
    fetchPolicy: 'network-only',
  });

  const {
    loading: loadingServicePassengers,
    error: errorServicePassengers,
    data: dataServicePassengers,
    refetch: refetchServicePassengers,
  } = servicePassengers;

  const passengers = dataServicePassengers?.service?.passengers || [];

  if (errorServicePassengers) {
    console.error(errorServicePassengers);
  }

  if (loadingServicePassengers) {
    return <Skeleton active={true} loading={true} />;
  }

  return (
    <PassengersManagement
      clientId={props.clientId}
      refetch={refetchServicePassengers}
      serviceId={props.serviceId}
      passengers={passengers}
    />
  );
};

class PassengersManagement extends React.Component<
  IPassengersManagementProps,
  IPassengersManagementState
> {
  private columns: any[];
  static contextType = MainContext;

  constructor(props: IPassengersManagementProps) {
    super(props);
    this.columns = [
      {
        title: 'Nombre',
        dataIndex: 'name',
        width: 300,
      },
      {
        title: 'Email',
        dataIndex: 'email',
      },
      {
        title: 'Teléfono',
        dataIndex: 'phone',
      },
      {
        title: 'Operaciones',
        dataIndex: 'operation',
        width: 100,
        render: (text: any, record: IRow) => {
          const menus = (
            <Menu>
              <Menu.Item
                onClick={() =>
                  sendEmail(constructEmail(this.props.serviceId, record.email))
                }
              >
                Enviar email
              </Menu.Item>
              <Menu.Item
                onClick={async () => {
                  const url = await getMobileApk('passenger');
                  const mailTo = `mailto:${
                    record.email
                  }?subject=Aplicación móvil&body=${encodeURIComponent(
                    url as string,
                  )}`;
                  sendEmail(mailTo);
                }}
              >
                Enviar aplicación móvil
              </Menu.Item>
            </Menu>
          );

          return this.state.dataSource.length >= 1 ? (
            <Dropdown overlay={menus}>
              <Button size="small">
                Acciones <DownOutlined translate={undefined} />
              </Button>
            </Dropdown>
          ) : null;
        },
      },
    ];

    const data =
      this.props.passengers && this.props.passengers.length
        ? this.props.passengers.map((passenger) => ({
            ...passenger,
            key: passenger.email,
          }))
        : [];

    const count = data.length;

    this.state = {
      dataSource: data,
      count,
      createPassengerDrawerVisible: false,
    };
  }

  render() {
    const { dataSource } = this.state;
    return (
      <div>
        <Table
          locale={{
            emptyText: (
              <Empty
                description="No hay pasajeros asignados al servicio"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size={'small'}
          bordered
          dataSource={dataSource}
          columns={this.columns}
        />
      </div>
    );
  }
}

interface IPassengersManagementDrawerProps extends DrawerProps {
  clientId: string;
  serviceId: string;
  visible: boolean;
  onClose: () => void;
}

export default class PassengersManagementDrawer extends React.Component<IPassengersManagementDrawerProps> {
  render() {
    const node =
      this.props.serviceId || this.props.clientId ? (
        <>
          <PassengersManagementContainer
            serviceId={this.props.serviceId}
            clientId={this.props.clientId}
          />
        </>
      ) : (
        <Spin
          style={{
            display: 'flex',
            height: '100vh',
            justifyContent: 'flex-end',
          }}
          size="large"
        />
      );

    return (
      <Drawer
        destroyOnClose={true}
        {...this.props}
        title={`Ver pasajeros del servicio ${this.props.serviceId}`}
        width={'50vw'}
        visible={this.props.visible}
        onClose={this.props.onClose}
      >
        {node}
      </Drawer>
    );
  }
}
